import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Save, X, LogOut, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function AdminMexicoGalleryPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingImage, setEditingImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const emptyImage = {
    image_url: '',
    alt_text: 'Film production in Mexico',
    active: true,
    order: 0
  };

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadImages();
  }, [navigate]);

  const loadImages = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/mexico-gallery`);
      const data = await response.json();
      setImages(data);
    } catch (err) {
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, WebP allowed.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/admin/upload-image?image_type=default`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
        } catch (e) {
          errorMessage = `Upload failed with status ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setEditingImage({ ...editingImage, image_url: data.url });
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = typeof err === 'string' ? err : (err.message || 'Failed to upload image');
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveImage = async () => {
    if (!editingImage.image_url) {
      toast.error('Image URL is required');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (editingImage.id) {
        // Update
        await fetch(`${backendUrl}/api/admin/mexico-gallery/${editingImage.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingImage)
        });
        toast.success('Image updated');
      } else {
        // Create
        await fetch(`${backendUrl}/api/admin/mexico-gallery`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingImage)
        });
        toast.success('Image added');
      }
      
      setEditingImage(null);
      loadImages();
    } catch (err) {
      toast.error('Failed to save image');
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/mexico-gallery/${id}`, { method: 'DELETE' });
      toast.success('Image deleted');
      loadImages();
    } catch (err) {
      toast.error('Failed to delete image');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bk-bg))] flex items-center justify-center">
        <p className="text-[hsl(var(--bk-fg-muted))]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))]">
      {/* Header */}
      <header className="border-b border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))] sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft size={16} /> Back to Admin
              </Button>
            </Link>
            <div>
              <h1 className="font-display text-2xl text-[hsl(var(--bk-fg))]">Mexico Gallery</h1>
              <p className="text-sm text-[hsl(var(--bk-fg-muted))]">Manage homepage Mexico carousel images</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              sessionStorage.removeItem('admin_auth');
              navigate('/admin');
            }}
            className="gap-2"
          >
            <LogOut size={16} /> Logout
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <p className="text-[hsl(var(--bk-fg-muted))]">
            These images appear in the Mexico Advantage carousel on the homepage ({images.filter(i => i.active).length} active)
          </p>
          <Button 
            onClick={() => {
              const nextOrder = images.length > 0 ? Math.max(...images.map(i => i.order)) + 1 : 0;
              setEditingImage({ ...emptyImage, order: nextOrder });
            }}
            className="gap-2"
            data-testid="add-gallery-image-btn"
          >
            <Plus size={16} /> Add Image
          </Button>
        </div>

        {/* Images Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {images.map((image) => (
            <div 
              key={image.id}
              className="border border-[hsl(var(--bk-line))] rounded-lg overflow-hidden bg-[hsl(var(--bk-bg-elev))] hover:border-[hsl(var(--bk-accent))]/30 transition-colors"
              data-testid={`gallery-image-${image.id}`}
            >
              <div className="aspect-square bg-black/20 relative">
                {image.image_url ? (
                  <img 
                    src={image.image_url} 
                    alt={image.alt_text} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x400/333/666?text=Broken+Image';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon size={48} className="text-[hsl(var(--bk-fg-subtle))]" />
                  </div>
                )}
                {!image.active && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white font-body text-sm px-3 py-1 bg-red-500 rounded">Inactive</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Order: {image.order}</span>
                  <span className={`text-xs px-2 py-1 rounded ${image.active ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                    {image.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-3 truncate">{image.alt_text}</p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setEditingImage(image)}
                    className="flex-1 gap-2"
                  >
                    <Edit size={14} /> Edit
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => handleDeleteImage(image.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {images.length === 0 && (
          <div className="text-center py-12 border border-dashed border-[hsl(var(--bk-line))] rounded-lg">
            <ImageIcon size={48} className="mx-auto text-[hsl(var(--bk-fg-subtle))] mb-4" />
            <p className="text-[hsl(var(--bk-fg-muted))] mb-4">No images in the gallery yet</p>
            <Button onClick={() => {
              const nextOrder = 0;
              setEditingImage({ ...emptyImage, order: nextOrder });
            }} className="gap-2">
              <Plus size={16} /> Add First Image
            </Button>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {editingImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setEditingImage(null)}>
          <div 
            className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-[hsl(var(--bk-fg))]">
                {editingImage.id ? 'Edit Image' : 'New Image'}
              </h2>
              <button onClick={() => setEditingImage(null)} className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))]">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Upload Image</Label>
                <Input
                  key={editingImage?.image_url || 'upload-input'}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleImageUpload(file);
                      // Reset input after reading
                      e.target.value = '';
                    }
                  }}
                  disabled={uploading}
                />
                {uploading && <p className="text-xs text-[hsl(var(--bk-accent))] mt-1">Uploading to Cloudflare R2...</p>}
                <p className="text-xs text-[hsl(var(--bk-fg-subtle))] mt-1">Max 10MB. JPEG, PNG, or WebP</p>
              </div>

              {editingImage.image_url && (
                <div className="border border-[hsl(var(--bk-line))] rounded-lg overflow-hidden">
                  <img src={editingImage.image_url} alt="Preview" className="w-full h-64 object-cover" />
                </div>
              )}

              <div>
                <Label>Image URL</Label>
                <Input
                  value={editingImage.image_url}
                  onChange={(e) => setEditingImage({ ...editingImage, image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div>
                <Label>Alt Text</Label>
                <Input
                  value={editingImage.alt_text}
                  onChange={(e) => setEditingImage({ ...editingImage, alt_text: e.target.value })}
                  placeholder="Film production in Mexico"
                />
              </div>

              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={editingImage.order}
                  onChange={(e) => setEditingImage({ ...editingImage, order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingImage.active}
                  onChange={(e) => setEditingImage({ ...editingImage, active: e.target.checked })}
                  className="rounded"
                />
                <Label>Active (show in carousel)</Label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSaveImage} className="flex-1 gap-2" disabled={uploading}>
                <Save size={16} /> Save Image
              </Button>
              <Button variant="outline" onClick={() => setEditingImage(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
